/**
 * PlaygroundPage — single purposeful page for running + visualizing agent executions.
 *
 * Replaces the old IntelligentExecutionPage (5 overlapping view modes) with:
 *   ┌─────────────────────────────────────────────────┐
 *   │ RunControls (agent picker, prompt, Run)          │  ← sticky top
 *   ├─────────────┬──────────────────┬────────────────┤
 *   │ History     │ Canvas           │ NodeDetail     │
 *   │ (sidebar)   │ Graph▸Tree▸      │ (docked right) │
 *   │             │ Timeline▸Prompts │                │
 *   └─────────────┴──────────────────┴────────────────┘
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/services/api'
import { useWebSocket } from '@/hooks/useWebSocket'
import ZiroGraphDiagram from '@/components/intelligent/ZiroGraphDiagram'
import NodeDetailPanel, { type NodeDetail } from '@/components/intelligent/NodeDetailPanel'
import ParallelTimeline, { type TimelineTask } from '@/components/intelligent/ParallelTimeline'
import ForkPromptModal, { type ForkPromptSource } from '@/components/playground/ForkPromptModal'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import {
  PlayIcon,
  ArrowPathIcon,
  SignalIcon,
  SignalSlashIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClipboardDocumentIcon,
  Bars3Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ListBulletIcon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import toast from 'react-hot-toast'

// ─────────────────────────────────────────────────────────────────────────────
// Types

interface TaskState {
  id: string
  agentName: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  group: number
  prompt?: string
  output?: string
  durationMs: number
  startMs: number
  tokens?: number
  cost?: number
  model?: string
  provider?: string
  error?: string
  description?: string
  langfuseTraceId?: string
}

interface OrchestrationState {
  requestId: string
  parentAgentName?: string   // the actual orchestrator that ran (≠ the dropdown)
  tasks: Record<string, TaskState>
  groups: number
  totalDurationMs: number
  phase: 'idle' | 'decomposing' | 'executing' | 'verifying' | 'synthesizing' | 'done'
  verificationVerdict?: string
}

type CanvasTab = 'graph' | 'tree' | 'timeline' | 'prompts'
type PageMode = 'live' | 'replay'

// ─────────────────────────────────────────────────────────────────────────────
// Page

export default function PlaygroundPage() {
  // Read initial agent from ?agent=... query param (e.g. when navigating from
  // WorkflowsPage's "View Graph" button)
  const [searchParams, setSearchParams] = useSearchParams()
  const initialAgent = searchParams.get('agent') || 'auto-orchestrator'

  // Run controls
  const [agentName, setAgentName] = useState<string>(initialAgent)

  // When the URL param changes, update the selected agent (and clear the
  // param afterwards so it doesn't override later user picks)
  useEffect(() => {
    const q = searchParams.get('agent')
    if (q && q !== agentName) {
      setAgentName(q)
      const next = new URLSearchParams(searchParams)
      next.delete('agent')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])
  const [prompt, setPrompt] = useState<string>('')
  const [contextJson, setContextJson] = useState<string>('{}')
  const [isRunning, setIsRunning] = useState(false)
  const [sandboxMode, setSandboxMode] = useState(false)
  const [runError, setRunError] = useState<string | null>(null)

  // video_gen preview pipeline (Phase 6: validate → thumbnail → full render)
  const [draftMode, setDraftMode] = useState(false)
  const [thumbUrl, setThumbUrl] = useState<string | null>(null)
  const [thumbMeta, setThumbMeta] = useState<{ render_ms?: number; total_ms?: number } | null>(null)
  const [thumbLoading, setThumbLoading] = useState(false)

  // UES Phase 0 — pre-decomposition context from NeuralGraph
  const [uesState, setUesState] = useState<{
    status: 'idle' | 'fetching' | 'ready' | 'gap'
    confidence?: number
    latencyMs?: number
    extractionId?: string | null
  }>({ status: 'idle' })

  // Layout
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [historySearch, setHistorySearch] = useState('')
  const [historyTestsOnly, setHistoryTestsOnly] = useState(false)
  const [expandedTestGroups, setExpandedTestGroups] = useState<Record<string, boolean>>({})
  const [canvasTab, setCanvasTab] = useState<CanvasTab>('graph')

  // If the current tab is Graph but the agent has no graph, fall back to Tree.
  // (effect added below once hasGraph is computed)
  const [mode, setMode] = useState<PageMode>('live')
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [replayRequestId, setReplayRequestId] = useState<string | null>(null)
  const [forkSource, setForkSource] = useState<ForkPromptSource | null>(null)

  // Live execution state
  const [liveState, setLiveState] = useState<OrchestrationState>({
    requestId: '',
    tasks: {},
    groups: 0,
    totalDurationMs: 0,
    phase: 'idle',
  })
  const orchestrationStartRef = useRef<number>(0)

  // Agents list for the picker
  const { data: agents = [] } = useQuery({
    queryKey: ['centralized-agents'],
    queryFn: () => apiClient.getCentralizedAgents({}),
    staleTime: 60_000,
  })

  // Workflow graphs (architecture-level, like the old IntelligentExecution page)
  const { data: workflowGraphs } = useQuery({
    queryKey: ['workflow-graphs'],
    queryFn: async () => {
      const resp = await (apiClient as any).client.get('/api/v1/workflows/graphs')
      return (resp.data?.graphs || {}) as Record<string, any>
    },
    staleTime: 5 * 60_000,
  })

  // Set of registered agent names — used to decide whether a workflow is
  // actually runnable.
  const agentNameSet = useMemo(() => new Set((agents || []).map((a: any) => a.name)), [agents])

  // Resolve the agent name to actually invoke for a given selection.
  // Workflows use diagram-style IDs (e.g. "embedding-agent", "project-health-
  // supervisor") that don't always match the registered agent name. This tries
  // progressively looser matches:
  //   1. The selection itself with -/_ variants
  //   2. The workflow's root nodes (no incoming edges) with suffix stripping
  //   3. Any node ID with suffix stripping
  //   4. Prefix match against registered agents (e.g. "n8n-workflow" → "n8n-workflow-builder")
  const resolveRunnableAgent = useMemo(() => (sel: string): string | null => {
    if (!sel) return null

    // 1. Prefer the backend-declared entry_agent on the workflow (authoritative).
    const wf = workflowGraphs?.[sel] as any
    if (wf?.entry_agent && agentNameSet.has(wf.entry_agent)) {
      return wf.entry_agent
    }

    // Fallback heuristics (for workflows that haven't declared entry_agent yet
    // or for raw agent selections — not a workflow).
    const variants = (s: string) => [
      s,
      s.replace(/_/g, '-'),
      s.replace(/-/g, '_'),
      s.replace(/-agent$|-supervisor$|-crew$|_supervisor$|_crew$|-crew-[0-9]+$/, ''),
    ].filter(Boolean)

    // 2. Direct variants of selection
    for (const v of variants(sel)) if (agentNameSet.has(v)) return v

    if (wf) {
      // 3. Root nodes (no incoming edges)
      const indeg: Record<string, number> = {}
      for (const n of (wf.nodes || [])) indeg[n.id] = 0
      for (const e of (wf.edges || [])) if (e.target in indeg) indeg[e.target]++
      const roots = Object.keys(indeg).filter(id => indeg[id] === 0)
      for (const r of roots) for (const v of variants(r)) if (agentNameSet.has(v)) return v

      // 4. Any node
      for (const n of (wf.nodes || [])) for (const v of variants(n.id)) if (agentNameSet.has(v)) return v
    }

    // 5. Prefix match
    for (const an of agentNameSet) {
      if (typeof an === 'string' && an.startsWith(sel)) return an
    }
    return null
  }, [workflowGraphs, agentNameSet])

  const runnableAgentName = useMemo(() => resolveRunnableAgent(agentName), [agentName, resolveRunnableAgent])
  const isRunnable = !!runnableAgentName

  // Per-agent graph (only used as fallback when no workflow graph matches)
  const { data: agentGraphData } = useQuery({
    queryKey: ['agent-graph', agentName],
    queryFn: () => apiClient.getAgentGraph(agentName).catch(() => null),
    enabled: !!agentName,
    staleTime: 60_000,
  })

  // Resolve which graph to show for the current agent:
  //   1. Direct workflow_graphs hit (auto-orchestrator, n8n-workflow, etc.)
  //   2. Match via agent metadata.architectures (when present)
  //   3. Scan workflow_graphs for any whose node IDs include the agent name
  //      (catches embedding-agent → drive-embedding, project-health-crew → project-health, etc.)
  //   4. Per-agent graph from /agents/centralized/{name}/graph
  //   5. undefined → graph tab is hidden
  const resolvedGraph = useMemo(() => {
    if (!agentName) return undefined
    if (workflowGraphs && workflowGraphs[agentName]) return workflowGraphs[agentName]

    const agentMeta: any = agents?.find((a: any) => a.name === agentName)
    const archs: string[] = agentMeta?.architectures || []
    for (const arch of archs) {
      if (workflowGraphs && workflowGraphs[arch]) return workflowGraphs[arch]
    }

    if (workflowGraphs) {
      const variants = [
        agentName,
        agentName.replace(/_/g, '-'),
        agentName.replace(/-/g, '_'),
        agentName.replace(/-agent$|-crew$/, ''),
      ]
      for (const wf of Object.values(workflowGraphs) as any[]) {
        const ids: string[] = (wf?.nodes || []).map((n: any) => n.id || '')
        if (variants.some(v => ids.includes(v))) return wf
      }
    }

    return agentGraphData || undefined
  }, [agentName, workflowGraphs, agents, agentGraphData])

  // Transform any incoming graph (workflow_graphs already in correct shape, or
  // per-agent shape) into ZiroGraphData. Returns undefined when no usable
  // graph data exists, so the Graph sub-tab is hidden entirely.
  const transformedGraph = useMemo(() => {
    if (!resolvedGraph) return undefined
    const g: any = resolvedGraph
    if (g?.layout?.layers) return g  // already in the right shape
    if (!Array.isArray(g.nodes) || g.nodes.length === 0) return undefined

    // Kahn's algorithm: layer N = nodes with all deps in layers <N
    const incoming: Record<string, Set<string>> = {}
    const outgoing: Record<string, Set<string>> = {}
    for (const n of g.nodes) {
      incoming[n.id] = new Set()
      outgoing[n.id] = new Set()
    }
    for (const e of (g.edges || [])) {
      if (incoming[e.target] && outgoing[e.source]) {
        incoming[e.target].add(e.source)
        outgoing[e.source].add(e.target)
      }
    }
    const layers: string[][] = []
    const placed = new Set<string>()
    while (placed.size < g.nodes.length) {
      const ready = g.nodes
        .filter((n: any) => !placed.has(n.id) && [...incoming[n.id]].every(p => placed.has(p)))
        .map((n: any) => n.id)
      if (ready.length === 0) break  // cycle — bail
      layers.push(ready)
      ready.forEach((id: string) => placed.add(id))
    }

    const nodes = g.nodes.map((n: any) => ({
      id: n.id,
      name: n.label || n.id,
      type: n.type || 'specialist',
      description: n.description || '',
      position: n.position || { x: 0, y: 0 },
      icon: n.icon || 'sparkles',
      status: n.status || 'idle',
    }))
    return {
      name: g.name || g.display_name || agentName,
      nodes,
      edges: (g.edges || []).map((e: any) => ({
        source: e.source,
        target: e.target,
        label: e.label || '',
        condition: e.condition || null,
        is_conditional: !!e.is_conditional,
      })),
      layout: { layers },
      // ZiroGraphDiagram footer reads node_groups.{specialists,reviews,...}
      // for stats — synthesize from node types so it doesn't crash.
      node_groups: {
        specialists: nodes.filter((n: any) => n.type === 'specialist' || n.type === 'execution'),
        reviews: nodes.filter((n: any) => n.type === 'review'),
        context: nodes.filter((n: any) => n.type === 'context'),
        compaction: nodes.filter((n: any) => n.type === 'compaction'),
      },
    }
  }, [resolvedGraph, agentName])

  // (executionGraphData + graphToShow + hasGraph defined below activeState
  // — they reference activeState which is computed later in this component.)

  // History for the sidebar
  const { data: history, refetch: refetchHistory } = useQuery({
    queryKey: ['execution-history-playground'],
    queryFn: async () => {
      const resp = await apiClient.getExecutionHistory({ limit: 100 })
      return resp.data || []
    },
    staleTime: 15_000,
  })

  // Auto-open the latest completed run on mount
  useEffect(() => {
    if (history && history.length > 0 && !replayRequestId && liveState.phase === 'idle') {
      const latest = history[0]
      if (latest?.request_id) {
        setReplayRequestId(latest.request_id)
        setMode('replay')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history])

  // ── WebSocket: live orchestration events ──
  const handleWsMessage = useCallback((msg: any) => {
    // UES Phase 0 events (before decomposition)
    if (msg.type === 'orchestration.context_fetching') {
      setUesState({ status: 'fetching' })
      return
    }
    if (msg.type === 'orchestration.context_ready') {
      setUesState({
        status: msg.success ? 'ready' : 'gap',
        confidence: msg.confidence,
        latencyMs: msg.latency_ms,
        extractionId: msg.extraction_id,
      })
      return
    }

    if (msg.type === 'orchestration.decomposed') {
      const now = Date.now()
      orchestrationStartRef.current = now
      const tasks: Record<string, TaskState> = {}
      for (const t of msg.tasks || []) {
        tasks[t.id] = {
          id: t.id,
          agentName: t.agent_name,
          status: 'pending',
          group: 0,
          durationMs: 0,
          startMs: 0,
          prompt: t.prompt,
          description: t.description,
        }
      }
      setLiveState({
        requestId: msg.request_id || '',
        tasks,
        groups: msg.group_count || 0,
        totalDurationMs: 0,
        phase: 'executing',
      })
      setMode('live')
    }

    if (msg.type === 'orchestration.task_dispatch') {
      setLiveState(prev => ({
        ...prev,
        tasks: {
          ...prev.tasks,
          ...(prev.tasks[msg.task_id] ? {
            [msg.task_id]: {
              ...prev.tasks[msg.task_id],
              status: 'running' as const,
              startMs: Date.now() - orchestrationStartRef.current,
            },
          } : {}),
        },
      }))
    }

    if (msg.type === 'orchestration.task_complete') {
      setLiveState(prev => {
        const existing = prev.tasks[msg.task_id]
        if (!existing) return prev
        return {
          ...prev,
          tasks: {
            ...prev.tasks,
            [msg.task_id]: {
              ...existing,
              status: msg.success ? 'completed' as const : 'failed' as const,
              durationMs: msg.elapsed_ms || (Date.now() - orchestrationStartRef.current - existing.startMs),
              output: msg.output_summary,
              tokens: msg.total_tokens,
              cost: msg.total_cost_usd,
              model: msg.model_used,
            },
          },
          totalDurationMs: Date.now() - orchestrationStartRef.current,
        }
      })
    }

    if (msg.type === 'orchestration.group_complete') {
      setLiveState(prev => ({
        ...prev,
        totalDurationMs: Date.now() - orchestrationStartRef.current,
        phase: msg.group === msg.total_groups ? 'verifying' : 'executing',
      }))
    }

    if (msg.type === 'execution.complete' && msg.execution_id === liveState.requestId) {
      setLiveState(prev => ({ ...prev, phase: 'done', totalDurationMs: msg.duration_ms || prev.totalDurationMs }))
      refetchHistory()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveState.requestId])

  const { isConnected } = useWebSocket({ onMessage: handleWsMessage })

  // ── Replay: load specific orchestration ──
  const { data: replayData } = useQuery({
    queryKey: ['replay-orchestration', replayRequestId],
    queryFn: () => replayRequestId ? apiClient.getOrchestrationDetail(replayRequestId) : null,
    enabled: !!replayRequestId && mode === 'replay',
  })

  const replayState = useMemo<OrchestrationState | null>(() => {
    if (!replayData) return null
    // Flat (non-orchestration) invocation: render as a single node
    if (!replayData.is_orchestration) {
      const parent = (replayData as any).parent || replayData
      const id = parent.request_id || 'single'
      return {
        requestId: parent.request_id,
        parentAgentName: parent.agent_name,
        tasks: {
          [id]: {
            id,
            agentName: parent.agent_name || 'agent',
            status: parent.success ? 'completed' : 'failed',
            group: 0,
            prompt: parent.prompt,
            output: parent.output_summary,
            durationMs: parent.duration_ms || 0,
            startMs: 0,
            tokens: parent.total_tokens,
            cost: parent.total_cost_usd,
            model: parent.model_used,
            provider: parent.provider_used,
            error: parent.error,
            langfuseTraceId: parent.langfuse_trace_id,
          },
        },
        groups: 1,
        totalDurationMs: parent.duration_ms || 0,
        phase: 'done',
      }
    }
    const parent = replayData.parent
    const tasks: Record<string, TaskState> = {}
    const subs = replayData.sub_tasks || []

    // Use real created_at timestamps so parallel sub-tasks appear side-by-side
    // instead of stacked. t=0 is anchored on the earliest sub-task; the parent
    // row's created_at is when logging finishes, not when the run started.
    const toEpoch = (iso: string | null | undefined) =>
      iso ? new Date(iso).getTime() : 0
    const earliest = subs.reduce(
      (min: number, s: any) => {
        const t = toEpoch(s.created_at)
        return t > 0 && (min === 0 || t < min) ? t : min
      },
      0,
    )
    let maxEnd = 0
    for (const sub of subs) {
      const taskId = sub.parent_task_id || sub.agent_name
      const dur = sub.duration_ms || 0
      const startAbs = toEpoch(sub.created_at)
      const startMs = startAbs > 0 && earliest > 0 ? Math.max(0, startAbs - earliest) : 0
      if (startMs + dur > maxEnd) maxEnd = startMs + dur
      tasks[taskId] = {
        id: taskId,
        agentName: sub.agent_name,
        status: sub.success ? 'completed' : 'failed',
        group: 0,
        prompt: sub.prompt,
        output: sub.output_summary,
        durationMs: dur,
        startMs,
        tokens: sub.total_tokens,
        cost: sub.total_cost_usd,
        model: sub.model_used,
        provider: sub.provider_used,
        error: sub.error,
        langfuseTraceId: sub.langfuse_trace_id,
      }
    }
    // Option A: inject the parent orchestrator row as a synthetic pseudo-task
    // keyed on "__orchestrator" so the dynamic graph's root node clicks
    // resolve to real data (prompt/output/tokens/cost). group=-1 keeps it
    // out of the sub-task grouping layers.
    if (parent) {
      tasks['__orchestrator'] = {
        id: '__orchestrator',
        agentName: parent.agent_name || 'orchestrator',
        status: parent.success ? 'completed' : 'failed',
        group: -1,
        prompt: parent.prompt,
        output: parent.output_summary,
        durationMs: parent.duration_ms || 0,
        startMs: 0,
        tokens: parent.total_tokens,
        cost: parent.total_cost_usd,
        model: parent.model_used,
        provider: parent.provider_used,
        error: parent.error,
        langfuseTraceId: parent.langfuse_trace_id,
      }
    }

    return {
      requestId: parent.request_id,
      parentAgentName: parent.agent_name,
      tasks,
      groups: Object.keys(tasks).length,
      totalDurationMs: maxEnd || parent.duration_ms || 0,
      phase: 'done',
      verificationVerdict: parent.verification_verdict,
    }
  }, [replayData])

  // Derive overridden node IDs for fork highlighting
  const forkInfo = useMemo(() => {
    if (!replayData?.is_orchestration) return null
    const parent = replayData?.parent
    if (!parent?.forked_from_request_id) return null
    const overrides = parent.prompt_overrides_json
    if (!overrides || typeof overrides !== 'object') return null
    return {
      forkedFrom: parent.forked_from_request_id as string,
      overriddenNodeIds: new Set(Object.keys(overrides)),
    }
  }, [replayData])

  const rawActiveState = mode === 'replay' && replayState ? replayState : liveState

  // ── Replay animation ──
  // In replay mode we can "play back" the run: walk through a virtual clock
  // and show each task transitioning pending → running → complete/failed as
  // it did originally. Users can pause, scrub, or change speed.
  const [replayPlaying, setReplayPlaying] = useState(false)
  const [replayTime, setReplayTime] = useState(0)     // ms into the run
  const [replaySpeed, setReplaySpeed] = useState(4)   // x real-time

  // Reset the virtual clock when a new replay is loaded
  useEffect(() => {
    setReplayTime(0)
    setReplayPlaying(false)
  }, [replayRequestId])

  // Tick the virtual clock while playing
  useEffect(() => {
    if (!replayPlaying || mode !== 'replay') return
    const total = rawActiveState.totalDurationMs || 0
    const interval = setInterval(() => {
      setReplayTime(t => {
        const next = t + 100 * replaySpeed
        if (next >= total) {
          setReplayPlaying(false)
          return total
        }
        return next
      })
    }, 100)
    return () => clearInterval(interval)
  }, [replayPlaying, replaySpeed, mode, rawActiveState.totalDurationMs])

  // Derive the "playing" task state: statuses computed from virtual clock
  const isAnimating = mode === 'replay' && (replayPlaying || (replayTime > 0 && replayTime < (rawActiveState.totalDurationMs || 0)))
  const activeState = useMemo(() => {
    if (!isAnimating) return rawActiveState
    const t = replayTime
    const playedTasks: Record<string, any> = {}
    for (const [id, task] of Object.entries(rawActiveState.tasks)) {
      const start = (task as any).startMs ?? 0
      const dur = (task as any).durationMs ?? 0
      let status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
      if (t < start) status = 'pending'
      else if (t < start + dur) status = 'running'
      else status = (task as any).status === 'failed' || (task as any).status === 'cancelled' ? (task as any).status : 'completed'
      playedTasks[id] = { ...task, status }
    }
    return {
      ...rawActiveState,
      tasks: playedTasks,
      totalDurationMs: t,
      phase: t >= (rawActiveState.totalDurationMs || 0) ? 'done' : 'executing',
    }
  }, [isAnimating, replayTime, rawActiveState])

  // Dynamic execution graph from real sub-tasks (replaces the static template
  // when an actual run is loaded). Each task = a node colored by its status.
  //
  // Root node shows the actual orchestrator that ran (parentAgentName), NOT
  // the dropdown state. Excludes group=-1 from sub-task layers because that
  // slot is reserved for the "__orchestrator" pseudo-task (see replayState /
  // handleRun — the parent row is injected there so clicking the root node
  // in the graph resolves to real prompt/output/tokens data).
  const executionGraphData = useMemo(() => {
    const allTasks = Object.values(activeState.tasks)
    // Sub-tasks only for the layer grouping (exclude the pseudo parent).
    const subTasks = allTasks.filter((t: any) => (t.group ?? 0) >= 0)
    if (subTasks.length === 0) return null

    const rootId = '__orchestrator'
    const rootLabel = activeState.parentAgentName || agentName || 'Orchestrator'
    const rootNode = {
      id: rootId,
      name: rootLabel,
      type: 'supervisor',
      description: '',
      position: { x: 0, y: 0 },
      icon: 'sparkles',
      status: activeState.phase === 'done' ? 'completed' : 'active',
    }

    const tasksByGroup: Record<number, typeof subTasks> = {}
    for (const t of subTasks) {
      const g = (t as any).group ?? 0
      if (!tasksByGroup[g]) tasksByGroup[g] = []
      tasksByGroup[g].push(t)
    }
    const groupKeys = Object.keys(tasksByGroup).map(Number).sort((a, b) => a - b)

    const taskNodes = subTasks.map((t: any) => ({
      id: t.id,
      name: t.agentName || t.id,
      type: 'specialist',
      description: t.id !== t.agentName ? t.id : '',
      position: { x: 0, y: 0 },
      icon: 'cpu',
      status: t.status === 'completed' ? 'completed'
            : t.status === 'failed' || t.status === 'cancelled' ? 'failed'
            : t.status === 'running' ? 'running'
            : 'idle',
    }))

    const edges: any[] = []
    for (const t of (tasksByGroup[groupKeys[0]] || [])) {
      edges.push({ source: rootId, target: t.id, label: '', condition: null, is_conditional: false })
    }
    for (let i = 1; i < groupKeys.length; i++) {
      for (const prev of tasksByGroup[groupKeys[i - 1]]) {
        for (const cur of tasksByGroup[groupKeys[i]]) {
          edges.push({ source: prev.id, target: cur.id, label: '', condition: null, is_conditional: false })
        }
      }
    }

    return {
      name: `${rootLabel} · ${subTasks.length} tasks`,
      nodes: [rootNode, ...taskNodes],
      edges,
      layout: {
        layers: [[rootId], ...groupKeys.map(g => tasksByGroup[g].map((t: any) => t.id))],
      },
      node_groups: {
        supervisors: [rootId],
        specialists: taskNodes.map(n => n.id),
      },
    }
  }, [activeState.tasks, activeState.phase, activeState.parentAgentName, agentName])

  const graphToShow = executionGraphData || transformedGraph
  const hasGraph = !!graphToShow

  useEffect(() => {
    if (!hasGraph && canvasTab === 'graph') setCanvasTab('tree')
  }, [hasGraph, canvasTab])

  // ── Derived: node status map, timeline, selected detail ──
  const nodeStatusMap = useMemo(() => {
    const map: Record<string, 'pending' | 'active' | 'completed' | 'error'> = {}
    for (const [id, task] of Object.entries(activeState.tasks)) {
      if (task.status === 'running') map[id] = 'active'
      else if (task.status === 'completed') map[id] = 'completed'
      else if (task.status === 'failed' || task.status === 'cancelled') map[id] = 'error'
      else map[id] = 'pending'
    }
    return map
  }, [activeState.tasks])

  // Exclude the __orchestrator pseudo-task (group=-1) from the timeline —
  // it's not a real sub-task step, it represents the parent orchestrator.
  const timelineTasks = useMemo<TimelineTask[]>(() =>
    Object.values(activeState.tasks)
      .filter(t => (t.group ?? 0) >= 0)
      .map(t => ({
        id: t.id,
        agentName: t.agentName,
        status: t.status,
        startMs: t.startMs,
        durationMs: t.durationMs,
        group: t.group,
      })),
  [activeState.tasks])

  const selectedDetail = useMemo<NodeDetail | null>(() => {
    if (!selectedNodeId) return null
    const task = activeState.tasks[selectedNodeId]
    if (!task) return null

    // Extract video filename from output for video_gen runs
    let videoPath: string | undefined
    if (task.agentName === 'video_gen' || task.agentName === 'video-generation') {
      const match = (task.output || '').match(/\/app\/video_output\/([^\s"']+\.mp4)/)
      if (match) videoPath = match[1]
    }

    // Extract argus recording id from output when the agent is argus.
    // record_demo_run returns {"recording_id": "<uuid>", ...} in agent_data,
    // which the execution_log output_summary serialises inline.
    let argusRecordingId: string | undefined
    if (task.agentName === 'argus') {
      const m = (task.output || '').match(
        /"recording_id"\s*:\s*"([0-9a-fA-F-]{32,36})"/,
      )
      if (m) argusRecordingId = m[1]
    }

    return {
      taskId: task.id,
      agentName: task.agentName,
      status: task.status,
      prompt: task.prompt,
      output: task.output,
      durationMs: task.durationMs,
      totalTokens: task.tokens,
      costUsd: task.cost,
      modelUsed: task.model,
      providerUsed: task.provider,
      error: task.error,
      description: task.description,
      langfuseTraceId: task.langfuseTraceId,
      videoPath,
      argusRecordingId,
      requestId: activeState.requestId || undefined,
    }
  }, [selectedNodeId, activeState.tasks])

  // ── Actions ──

  const handleRun = async () => {
    if (!prompt.trim() || !agentName) {
      toast.error('Pick an agent and enter a prompt')
      return
    }
    let parsedCtx: any = {}
    try { parsedCtx = contextJson.trim() ? JSON.parse(contextJson) : {} }
    catch { toast.error('Context must be valid JSON'); return }

    setIsRunning(true)
    setRunError(null)
    setUesState({ status: 'idle' })
    setMode('live')
    // Invoke via the resolved agent name (workflow ids like "drive-embedding"
    // may need to call a differently-named registered agent like "embedding").
    const invokeName = runnableAgentName || agentName
    setLiveState({
      requestId: '',
      parentAgentName: invokeName,   // freeze the true orchestrator at invoke time
      tasks: {},
      groups: 0,
      totalDurationMs: 0,
      phase: 'decomposing',
    })

    try {
      if (sandboxMode) {
        parsedCtx._zbox_sandbox = true
        parsedCtx._zbox_template = 'zbox-general'
      }
      const isVideoGen = invokeName === 'video_gen' || invokeName === 'video-generation'
      if (isVideoGen && draftMode) {
        parsedCtx._draft = true
      }
      const resp = await (apiClient as any).client.post(`/api/v1/agents/${invokeName}/invoke`, {
        prompt,
        context: parsedCtx,
      })
      const data = resp.data
      if (data?.request_id) {
        setLiveState(prev => ({ ...prev, requestId: data.request_id, phase: 'done' }))
        // Refresh history so the run appears in sidebar
        setTimeout(() => refetchHistory(), 500)
        toast.success(`Run complete · ${data.duration_ms || 0}ms`)
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Run failed'
      setRunError(msg)
      toast.error(msg)
      setLiveState(prev => ({ ...prev, phase: 'idle' }))
    } finally {
      setIsRunning(false)
    }
  }

  const handleReplay = (requestId: string) => {
    setMode('replay')
    setReplayRequestId(requestId)
    setSelectedNodeId(null)
  }

  // Phase 6: preview the current video_gen prompt as a single frame (PNG).
  const handlePreviewThumbnail = async () => {
    if (!prompt.trim()) { toast.error('Enter a video_gen prompt first'); return }
    setThumbLoading(true)
    setThumbUrl(null)
    setThumbMeta(null)
    try {
      // Parse config to pass to /videos/preview
      let cfg: any = {}
      try { cfg = JSON.parse(prompt) } catch { cfg = { description: prompt } }
      const preview = await (apiClient as any).client.post('/api/v1/videos/preview', cfg)
      const pv = preview.data
      if (!pv?.composition_id || !pv?.props) {
        toast.error(pv?.error || 'Preview returned no composition')
        return
      }
      const thumb = await (apiClient as any).client.post('/api/v1/videos/thumbnail', {
        composition_id: pv.composition_id,
        props: pv.props,
        frame: 0,
        image_format: 'png',
      })
      const t = thumb.data
      setThumbUrl(t.thumbnail_url)
      setThumbMeta({ render_ms: t.render_ms, total_ms: t.total_ms })
      toast.success(`Thumbnail rendered in ${t.total_ms}ms`)
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Thumbnail failed'
      toast.error(msg)
    } finally {
      setThumbLoading(false)
    }
  }

  const isVideoGenAgent = agentName === 'video_gen' || agentName === 'video-generation'

  // ── Render ──

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] -mx-4 -my-6 sm:-mx-6 lg:-mx-8">
      {/* ─── Sticky top: RunControls ─── */}
      <Card className="mx-4 sm:mx-6 lg:mx-8 mt-4 mb-3 shrink-0">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-foreground">Playground</h1>
            <span className="text-xs text-muted-foreground">run · watch · iterate</span>
            <div className="ml-auto flex items-center gap-2">
              {mode === 'live' && (
                <span className={clsx('flex items-center gap-1 text-xs', isConnected ? 'text-emerald-500' : 'text-red-500')}>
                  {isConnected ? <SignalIcon className="w-3 h-3" /> : <SignalSlashIcon className="w-3 h-3" />}
                  {isConnected ? 'Connected' : 'Disconnected'}
                </span>
              )}
              <div className="inline-flex rounded-lg bg-muted p-0.5">
                <button
                  onClick={() => setMode('live')}
                  className={clsx(
                    'px-2.5 py-1 text-xs font-medium rounded-md transition-all',
                    mode === 'live' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
                  )}
                >
                  <SignalIcon className="w-3.5 h-3.5 inline mr-1" />Live
                </button>
                <button
                  onClick={() => setMode('replay')}
                  className={clsx(
                    'px-2.5 py-1 text-xs font-medium rounded-md transition-all',
                    mode === 'replay' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
                  )}
                >
                  <ArrowPathIcon className="w-3.5 h-3.5 inline mr-1" />Replay
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-12 sm:col-span-3">
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">Agent</label>
              <select
                data-testid="playground-agent-select"
                value={agentName}
                onChange={e => setAgentName(e.target.value)}
                className="w-full text-xs px-2 py-2 rounded-md border border-border bg-card"
              >
                {(() => {
                  // Workflows = every entry from /workflows/graphs (the same
                  // source as the Workflows page). Always listed as its own
                  // option so the user can visualize every workflow even when
                  // its entry-point agent isn't registered yet.
                  const wfEntries = Object.entries(workflowGraphs || {}) as Array<[string, any]>
                  const wfKeys = new Set(wfEntries.map(([k]) => k))
                  const wfNodeIds = new Set<string>()
                  for (const [, wf] of wfEntries) {
                    for (const n of (wf?.nodes || [])) if (n?.id) wfNodeIds.add(n.id)
                  }
                  // Single agents = registered agents that are NOT a workflow
                  // and NOT a node inside any workflow.
                  const isPartOfWorkflow = (a: any) => {
                    const variants = [a.name, a.name?.replace(/_/g, '-'), a.name?.replace(/-/g, '_')]
                    return variants.some(v => wfKeys.has(v) || wfNodeIds.has(v))
                  }
                  const singles = (agents || []).filter((a: any) => !isPartOfWorkflow(a))
                  return (
                    <>
                      {wfEntries.length > 0 && (
                        <optgroup label={`── Workflows (${wfEntries.length})`}>
                          {wfEntries.map(([id, wf]) => {
                            const runnable = resolveRunnableAgent(id)
                            return (
                              <option
                                key={id}
                                value={id}
                                data-testid={`playground-agent-option-${id}`}
                              >
                                {wf?.name || id}{!runnable ? ' (template)' : ''}
                              </option>
                            )
                          })}
                        </optgroup>
                      )}
                      {singles.length > 0 && (
                        <optgroup label={`── Single agents (${singles.length})`}>
                          {singles.map((a: any) => (
                            <option
                              key={a.name}
                              value={a.name}
                              data-testid={`playground-agent-option-${a.name}`}
                            >
                              {a.display_name || a.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  )
                })()}
              </select>
            </div>
            <div className="col-span-12 sm:col-span-7">
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">Prompt</label>
              <textarea
                data-testid="playground-prompt"
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                rows={2}
                placeholder="What do you want this agent to do?"
                className="w-full text-xs px-2 py-2 rounded-md border border-border bg-card font-mono resize-none"
              />
            </div>
            <div className="col-span-12 sm:col-span-2 flex flex-col justify-end gap-1.5">
              <Button
                data-testid="playground-submit"
                onClick={handleRun}
                disabled={isRunning || !prompt.trim() || !isRunnable}
                className="w-full"
                title={!isRunnable ? 'Template workflow — no runnable entry-point agent yet (visualization only)' : undefined}
              >
                {isRunning ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <PlayIcon className="w-4 h-4" />}
                <span className="ml-1.5">{isRunning ? 'Running…' : isRunnable ? 'Run' : 'Template'}</span>
              </Button>
              <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground cursor-pointer" title="Run inside an isolated ZBox sandbox with audit trail + resource tracking">
                <input
                  type="checkbox"
                  checked={sandboxMode}
                  onChange={e => setSandboxMode(e.target.checked)}
                  className="w-3 h-3 rounded border-border"
                />
                Sandbox
              </label>
              {isVideoGenAgent && (
                <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground cursor-pointer" title="Render at 640x360 instead of 1920x1080 — ~4x faster for iteration">
                  <input
                    type="checkbox"
                    checked={draftMode}
                    onChange={e => setDraftMode(e.target.checked)}
                    className="w-3 h-3 rounded border-border"
                  />
                  Draft render
                </label>
              )}
            </div>
          </div>

          {isVideoGenAgent && (
            <div className="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-2">
              <button
                onClick={handlePreviewThumbnail}
                disabled={thumbLoading || !prompt.trim()}
                className="text-[11px] font-medium px-2.5 py-1 rounded border border-border bg-card hover:bg-muted disabled:opacity-50 inline-flex items-center gap-1.5"
                title="Stage 2: render frame 0 as PNG (~2-3s) to validate before full render"
              >
                {thumbLoading ? <ArrowPathIcon className="w-3 h-3 animate-spin" /> : <RectangleGroupIcon className="w-3 h-3" />}
                {thumbLoading ? 'Rendering thumb…' : 'Preview thumbnail'}
              </button>
              {thumbUrl ? (
                <div className="flex items-center gap-2">
                  <img src={thumbUrl} alt="thumbnail" className="h-14 rounded border border-border" />
                  <div className="text-[10px] text-muted-foreground">
                    <div>Frame 0 · {thumbMeta?.total_ms}ms total</div>
                    <div>render {thumbMeta?.render_ms}ms</div>
                  </div>
                </div>
              ) : (
                <p className="text-[10px] text-muted-foreground self-center">
                  Render a single frame before committing to the full video (Phase 6 iteration).
                </p>
              )}
            </div>
          )}

          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Context (JSON) — optional
            </summary>
            <textarea
              value={contextJson}
              onChange={e => setContextJson(e.target.value)}
              rows={3}
              className="mt-2 w-full text-xs px-2 py-2 rounded-md border border-border bg-card font-mono resize-y"
            />
          </details>

          {runError && <p className="text-xs text-red-500">{runError}</p>}

          {/* UES Phase 0 status — pre-decomposition context from NeuralGraph */}
          {uesState.status === 'fetching' && (
            <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-purple-500/10 text-purple-600 border border-purple-500/20">
              <ArrowPathIcon className="w-3.5 h-3.5 animate-spin shrink-0" />
              <span>Gathering context from NeuralGraph…</span>
              <span className="text-purple-500/60 ml-1">(up to 30s)</span>
            </div>
          )}
          {uesState.status === 'ready' && uesState.confidence !== undefined && (
            <div className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
              <span>Context ready · confidence {uesState.confidence.toFixed(2)}</span>
              {uesState.latencyMs != null && (
                <span className="text-emerald-500/60 ml-auto">{(uesState.latencyMs / 1000).toFixed(1)}s</span>
              )}
            </div>
          )}
          {uesState.status === 'gap' && (
            <div className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg bg-amber-500/10 text-amber-600 border border-amber-500/20">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
              <span>Context unavailable — proceeding without NeuralGraph context</span>
            </div>
          )}
        </div>
      </Card>

      {/* ─── Main 2-row layout: full-width canvas on top, history+detail below ─── */}
      <div className="flex-1 flex flex-col min-h-0 mx-4 sm:mx-6 lg:mx-8 mb-4 gap-3">

        {/* Row 1: Canvas (full width) */}
        <Card className="flex-1 min-h-[400px] flex flex-col !p-0">
          {/* Canvas sub-tabs */}
          <div className="flex items-center gap-1 px-3 py-2 border-b border-border shrink-0">
            {(([
              hasGraph ? { id: 'graph' as const, label: 'Graph', icon: RectangleGroupIcon } : null,
              { id: 'tree' as const, label: 'Tree', icon: Bars3Icon },
              { id: 'timeline' as const, label: 'Timeline', icon: ClockIcon },
              { id: 'prompts' as const, label: 'Prompts', icon: DocumentTextIcon },
            ] as const).filter(Boolean) as Array<{id: CanvasTab, label: string, icon: any}>).map(tab => (
              <button
                key={tab.id}
                onClick={() => setCanvasTab(tab.id)}
                className={clsx(
                  'px-2.5 py-1 text-xs font-medium rounded-md transition-all flex items-center gap-1',
                  canvasTab === tab.id ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-muted/50'
                )}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}

            {/* Replay animation controls — only in replay mode with a real run loaded */}
            {mode === 'replay' && rawActiveState.totalDurationMs > 0 && (
              <div className="ml-auto flex items-center gap-2 text-xs">
                <button
                  onClick={() => {
                    if (replayTime >= rawActiveState.totalDurationMs) setReplayTime(0)
                    setReplayPlaying(p => !p)
                  }}
                  className={clsx(
                    'px-2 py-1 rounded font-medium',
                    replayPlaying ? 'bg-amber-500/15 text-amber-600' : 'bg-primary/15 text-primary'
                  )}
                  title={replayPlaying ? 'Pause' : 'Play animation'}
                >
                  {replayPlaying ? '⏸ Pause' : '▶ Play'}
                </button>
                <input
                  type="range"
                  min={0}
                  max={rawActiveState.totalDurationMs}
                  step={100}
                  value={replayTime}
                  onChange={e => { setReplayTime(Number(e.target.value)); setReplayPlaying(false) }}
                  className="w-40"
                  title="Scrub through the run"
                />
                <span className="text-[10px] text-muted-foreground font-mono min-w-[80px]">
                  {(replayTime / 1000).toFixed(1)}s / {(rawActiveState.totalDurationMs / 1000).toFixed(1)}s
                </span>
                <select
                  value={replaySpeed}
                  onChange={e => setReplaySpeed(Number(e.target.value))}
                  className="text-[10px] px-1 py-0.5 rounded border border-border bg-card"
                  title="Playback speed"
                >
                  <option value={1}>1×</option>
                  <option value={2}>2×</option>
                  <option value={4}>4×</option>
                  <option value={10}>10×</option>
                </select>
                <button
                  onClick={() => { setReplayTime(0); setReplayPlaying(false) }}
                  className="text-muted-foreground hover:text-foreground text-xs"
                  title="Reset to start"
                >↺</button>
                {activeState.verificationVerdict && (
                  <span className={clsx(
                    'px-1.5 py-0.5 rounded-full text-[10px] font-medium ml-1',
                    activeState.verificationVerdict === 'PASS' ? 'bg-emerald-500/15 text-emerald-600' :
                    activeState.verificationVerdict === 'FAIL' ? 'bg-red-500/15 text-red-600' :
                    'bg-amber-500/15 text-amber-600'
                  )}>
                    {activeState.verificationVerdict}
                  </span>
                )}
              </div>
            )}

            {/* Phase/timing indicator (live mode, or replay with no duration) */}
            {activeState.phase !== 'idle' && !(mode === 'replay' && rawActiveState.totalDurationMs > 0) && (
              <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
                <span className="capitalize">{activeState.phase}</span>
                <span>·</span>
                <span>{(activeState.totalDurationMs / 1000).toFixed(1)}s</span>
                {activeState.verificationVerdict && (
                  <span className={clsx(
                    'px-1.5 py-0.5 rounded-full text-[10px] font-medium',
                    activeState.verificationVerdict === 'PASS' ? 'bg-emerald-500/15 text-emerald-600' :
                    activeState.verificationVerdict === 'FAIL' ? 'bg-red-500/15 text-red-600' :
                    'bg-amber-500/15 text-amber-600'
                  )}>
                    {activeState.verificationVerdict}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Canvas body */}
          <div className="flex-1 overflow-auto p-3">
            {canvasTab === 'graph' && (
              <div
                className="overflow-x-auto"
                onClick={(e) => {
                  const target = e.target as HTMLElement
                  const nodeEl = target.closest('[data-node-id]')
                  if (nodeEl) {
                    const nodeId = nodeEl.getAttribute('data-node-id')
                    if (nodeId) setSelectedNodeId(nodeId)
                  }
                }}
              >
                {forkInfo && mode === 'replay' && (
                  <div className="mb-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded-lg text-xs text-purple-300 flex items-center gap-2">
                    <span className="font-semibold">Fork</span>
                    <span className="text-purple-400/70">from {forkInfo.forkedFrom.slice(0, 16)}…</span>
                    <span className="text-purple-400/70">·</span>
                    <span>modified: {[...forkInfo.overriddenNodeIds].join(', ')}</span>
                  </div>
                )}
                <ZiroGraphDiagram
                  key={`graph-${agentName}-${activeState.requestId || 'idle'}-${Object.keys(activeState.tasks).length}`}
                  activeNodes={nodeStatusMap}
                  currentPhase={
                    activeState.phase === 'done' ? 'completed'
                    : activeState.phase === 'executing' ? 'active'
                    : activeState.phase
                  }
                  showFullGraph={true}
                  customGraphData={graphToShow}
                  compact
                  overriddenNodeIds={forkInfo?.overriddenNodeIds}
                />
                {timelineTasks.length > 0 && (
                  <div className="mt-3">
                    <ParallelTimeline
                      tasks={timelineTasks}
                      totalDurationMs={activeState.totalDurationMs}
                      onTaskClick={setSelectedNodeId}
                    />
                  </div>
                )}
              </div>
            )}

            {canvasTab === 'tree' && <ExecutionTreeView state={activeState} selectedId={selectedNodeId} onSelect={setSelectedNodeId} />}

            {canvasTab === 'timeline' && (
              timelineTasks.length > 0 ? (
                <ParallelTimeline
                  tasks={timelineTasks}
                  totalDurationMs={activeState.totalDurationMs}
                  onTaskClick={setSelectedNodeId}
                />
              ) : (
                <EmptyState mode={mode} />
              )
            )}

            {canvasTab === 'prompts' && <PromptsList state={activeState} onSelect={setSelectedNodeId} mode={mode} />}
          </div>
        </Card>

        {/* Row 2: History + NodeDetailPanel side-by-side */}
        <div className="flex gap-3 shrink-0 h-[280px]">

        {/* History sidebar */}
        <div className={clsx(
          'transition-all duration-200 shrink-0',
          sidebarOpen ? 'w-72' : 'w-10'
        )}>
          <Card className="h-full overflow-hidden flex flex-col !p-0">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="flex items-center gap-2 px-3 py-2 border-b border-border hover:bg-muted/50 transition-colors shrink-0"
            >
              {sidebarOpen ? <ChevronLeftIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
              {sidebarOpen && <span className="text-xs font-semibold flex-1 text-left">History</span>}
              {sidebarOpen && (
                <span
                  role="button"
                  className="text-[10px] text-muted-foreground hover:text-foreground"
                  onClick={(e) => { e.stopPropagation(); refetchHistory() }}
                  title="Refresh history"
                >
                  ↻
                </span>
              )}
            </button>
            {sidebarOpen && (
              <>
                {/* Search + filter controls */}
                <div className="px-2 py-2 border-b border-border space-y-1.5 shrink-0">
                  <input
                    type="text"
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    placeholder="Search agent / test id / prompt…"
                    className="w-full text-[11px] px-2 py-1.5 rounded border border-border bg-card"
                  />
                  <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={historyTestsOnly}
                      onChange={(e) => setHistoryTestsOnly(e.target.checked)}
                      className="rounded border-border"
                    />
                    Test runs only
                  </label>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {(() => {
                    if (!history || history.length === 0) {
                      return <p className="p-3 text-xs text-muted-foreground text-center">No runs in last 30 days. Run an agent above.</p>
                    }
                    const matchesSearch = (run: any) => {
                      if (!historySearch.trim()) return true
                      const q = historySearch.toLowerCase()
                      const hay = [run.agent_name, run.test_id, run.prompt, run.request_id, run.source].filter(Boolean).join(' ').toLowerCase()
                      return hay.includes(q)
                    }
                    const allFiltered = history.filter(matchesSearch)
                    const testRuns = allFiltered.filter((r: any) => r.test_id || r.source === 'test-harness')
                    const otherRuns = historyTestsOnly ? [] : allFiltered.filter((r: any) => !(r.test_id || r.source === 'test-harness'))

                    if (allFiltered.length === 0) {
                      return <p className="p-3 text-xs text-muted-foreground text-center">No runs match filter.<br/><span className="text-[10px]">{history.length} total in last 30d</span></p>
                    }

                    // Group test runs by base test_id (e.g. "1.1" and "1.1-v2" both group under "1.1")
                    const testGroups: Record<string, any[]> = {}
                    for (const run of testRuns) {
                      const tid = run.test_id || 'untagged'
                      const baseTid = tid.replace(/-v\d+$/, '')
                      if (!testGroups[baseTid]) testGroups[baseTid] = []
                      testGroups[baseTid].push(run)
                    }
                    // Sort each group: latest first
                    for (const k of Object.keys(testGroups)) {
                      testGroups[k].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
                    }
                    // Sort groups by test id naturally
                    const sortedTestKeys = Object.keys(testGroups).sort((a, b) => {
                      const aParts = a.split('.').map(n => parseInt(n) || 0)
                      const bParts = b.split('.').map(n => parseInt(n) || 0)
                      return aParts[0] !== bParts[0] ? aParts[0] - bParts[0] : (aParts[1] || 0) - (bParts[1] || 0)
                    })

                    const renderRow = (run: any, indent: boolean = false) => (
                      <button
                        key={run.request_id}
                        onClick={() => handleReplay(run.request_id)}
                        className={clsx(
                          'w-full text-left py-1.5 hover:bg-muted/50 transition-colors border-b border-border/50',
                          indent ? 'pl-8 pr-3' : 'px-3',
                          replayRequestId === run.request_id && 'bg-primary/10'
                        )}
                      >
                        <div className="flex items-center gap-1.5">
                          {run.success ? (
                            <CheckCircleIcon className="w-3 h-3 text-emerald-500 shrink-0" />
                          ) : (
                            <XCircleIcon className="w-3 h-3 text-red-500 shrink-0" />
                          )}
                          <span className="text-[11px] font-medium text-foreground truncate flex-1">{run.agent_name}</span>
                          {run.forked_from_request_id && (
                            <span className="text-[9px] px-1 rounded bg-purple-500/15 text-purple-600 shrink-0">fork</span>
                          )}
                          {run.test_id && !indent && (
                            <span className="text-[9px] px-1 rounded bg-blue-500/15 text-blue-600 shrink-0 font-mono">T{run.test_id}</span>
                          )}
                          {run.ues_extraction_id && (
                            <span className="text-[9px] px-1 rounded bg-emerald-500/15 text-emerald-600 shrink-0 font-mono">UES</span>
                          )}
                          {run.verification_verdict && (
                            <span className={clsx('text-[9px] px-1 rounded shrink-0 font-mono',
                              run.verification_verdict === 'PASS' ? 'bg-emerald-500/15 text-emerald-600' :
                              run.verification_verdict === 'PARTIAL' ? 'bg-amber-500/15 text-amber-600' :
                              'bg-red-500/15 text-red-600')}>{run.verification_verdict}</span>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground truncate mt-0.5">{run.prompt?.slice(0, 56) || '—'}</p>
                        <div className="flex items-center gap-2 mt-0.5 text-[9px] text-muted-foreground">
                          {run.duration_ms && <span>{(run.duration_ms / 1000).toFixed(1)}s</span>}
                          {run.total_tokens > 0 && <span>{run.total_tokens} tok</span>}
                          {run.created_at && <span>{new Date(run.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
                        </div>
                      </button>
                    )

                    return (<>
                      {testRuns.length > 0 && (
                        <div>
                          <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide bg-muted/30 border-b border-border/50">
                            Test runs · {sortedTestKeys.length} unique · {testRuns.length} total
                          </div>
                          {sortedTestKeys.map(tid => {
                            const group = testGroups[tid]
                            const expanded = expandedTestGroups[tid] || false
                            const latest = group[0]
                            return (
                              <div key={tid}>
                                <div className="flex items-stretch border-b border-border/50">
                                  {group.length > 1 && (
                                    <button
                                      onClick={() => setExpandedTestGroups(p => ({ ...p, [tid]: !expanded }))}
                                      className="px-2 hover:bg-muted/50 text-muted-foreground"
                                      title={expanded ? 'Collapse' : `Show ${group.length - 1} earlier rerun(s)`}
                                    >
                                      <span className="text-[10px]">{expanded ? '▾' : '▸'}</span>
                                    </button>
                                  )}
                                  {group.length === 1 && <div className="w-6" />}
                                  <div className="flex-1 min-w-0 relative">
                                    {renderRow(latest)}
                                    {group.length > 1 && !expanded && (
                                      <span className="absolute right-3 top-1.5 text-[9px] text-muted-foreground">+{group.length - 1}</span>
                                    )}
                                  </div>
                                </div>
                                {expanded && group.slice(1).map(r => renderRow(r, true))}
                              </div>
                            )
                          })}
                        </div>
                      )}
                      {otherRuns.length > 0 && (
                        <div>
                          <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide bg-muted/30 border-b border-border/50">
                            Other runs · {otherRuns.length}
                          </div>
                          {otherRuns.map((r: any) => renderRow(r))}
                        </div>
                      )}
                    </>)
                  })()}
                </div>
              </>
            )}
          </Card>
        </div>

        {/* Docked NodeDetailPanel — fills the rest of Row 2 */}
        <Card className="flex-1 min-w-0 !p-0 overflow-hidden">
          {selectedDetail ? (
            <NodeDetailPanel
              node={selectedDetail}
              mode="docked"
              onClose={() => setSelectedNodeId(null)}
              onEditAndRun={(node) => {
                // Determine if this node is part of an orchestration replay
                const parentReqId = mode === 'replay' ? replayRequestId || undefined : activeState.requestId || undefined
                const isSubtask = mode === 'replay' && !!replayData?.is_orchestration
                setForkSource({
                  taskId: node.taskId,
                  agentName: node.agentName,
                  prompt: node.prompt || '',
                  output: node.output,
                  totalTokens: node.totalTokens,
                  costUsd: node.costUsd,
                  durationMs: node.durationMs,
                  parentRequestId: parentReqId,
                  isOrchestrationSubtask: isSubtask,
                })
              }}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center p-6 text-center text-muted-foreground">
              <RectangleGroupIcon className="w-8 h-8 mb-2 opacity-40" />
              {Object.keys(activeState.tasks).length === 0 ? (
                <>
                  <p className="text-xs font-medium text-foreground mb-1">No execution loaded</p>
                  <p className="text-[11px]">Run an agent above, or pick a past run from History.</p>
                </>
              ) : selectedNodeId ? (
                <p className="text-xs">Selected node has no data — pick a different one.</p>
              ) : (
                <p className="text-xs">Click a node in the Graph / Tree to inspect its prompt, output, tokens, and cost.</p>
              )}
            </div>
          )}
        </Card>
        </div>
      </div>

      {/* Fork & Edit modal — Phase 2 + Phase 3 */}
      <ForkPromptModal
        source={forkSource}
        onClose={() => setForkSource(null)}
        onForkComplete={() => { refetchHistory() }}
      />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// ExecutionTreeView — Claude-Code-style nested tree

function ExecutionTreeView({
  state,
  selectedId,
  onSelect,
}: {
  state: OrchestrationState
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  const tasks = Object.values(state.tasks)
  if (tasks.length === 0) return <EmptyState mode="tree" />

  return (
    <div className="space-y-1 font-mono text-xs">
      <div className="flex items-center gap-2 py-1.5 px-2 rounded bg-primary/5">
        <span className="text-primary">▾</span>
        <span className="font-semibold text-foreground">
          agent.{state.requestId ? 'orchestration' : 'run'}
        </span>
        {state.totalDurationMs > 0 && (
          <span className="text-muted-foreground">{(state.totalDurationMs / 1000).toFixed(1)}s</span>
        )}
      </div>
      {tasks.map(task => (
        <button
          key={task.id}
          onClick={() => onSelect(task.id)}
          className={clsx(
            'w-full flex items-center gap-2 py-1.5 px-2 pl-6 rounded transition-colors text-left',
            selectedId === task.id ? 'bg-primary/15' : 'hover:bg-muted/50'
          )}
        >
          <span className={clsx(
            'w-2 h-2 rounded-full shrink-0',
            task.status === 'running' ? 'bg-amber-500 animate-pulse' :
            task.status === 'completed' ? 'bg-emerald-500' :
            task.status === 'failed' ? 'bg-red-500' :
            'bg-muted'
          )} />
          <span className="text-foreground font-medium truncate">{task.agentName}</span>
          <span className="text-muted-foreground truncate flex-1">{task.id}</span>
          {task.durationMs > 0 && (
            <span className="text-muted-foreground text-[10px] shrink-0">{(task.durationMs / 1000).toFixed(1)}s</span>
          )}
          {task.tokens != null && task.tokens > 0 && (
            <span className="text-muted-foreground text-[10px] shrink-0">{task.tokens} tok</span>
          )}
        </button>
      ))}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// PromptsList — flat sequential view

function PromptsList({
  state,
  onSelect,
  mode,
}: {
  state: OrchestrationState
  onSelect: (id: string) => void
  mode: PageMode
}) {
  const steps = Object.values(state.tasks).filter(t => t.prompt)
  if (steps.length === 0) return <EmptyState mode={mode} />

  return (
    <div className="space-y-3">
      {steps.map((step, i) => (
        <div key={step.id} className="border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => onSelect(step.id)}
            className="w-full flex items-center justify-between px-3 py-2 bg-muted/30 hover:bg-muted/50"
          >
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground">{i + 1}.</span>
              <span className="text-xs font-semibold text-foreground">{step.id}</span>
              <span className="text-[10px] text-muted-foreground">({step.agentName})</span>
            </div>
            <div className="flex items-center gap-2">
              {step.durationMs > 0 && (
                <span className="text-[10px] text-muted-foreground">{(step.durationMs / 1000).toFixed(1)}s</span>
              )}
              <span className={clsx(
                'text-[10px] font-medium px-1.5 py-0.5 rounded-full',
                step.status === 'completed' ? 'bg-emerald-500/15 text-emerald-600' :
                step.status === 'failed' ? 'bg-red-500/15 text-red-600' :
                'bg-muted text-muted-foreground'
              )}>
                {step.status}
              </span>
            </div>
          </button>
          <div className="p-3 space-y-2">
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Prompt</p>
              <pre className="text-xs bg-muted/30 rounded p-2 whitespace-pre-wrap max-h-32 overflow-y-auto font-mono">{step.prompt}</pre>
            </div>
            {step.output && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Output</p>
                <pre className="text-xs bg-emerald-500/5 border border-emerald-500/10 rounded p-2 whitespace-pre-wrap max-h-32 overflow-y-auto font-mono">{step.output}</pre>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

function EmptyState({ mode }: { mode: string }) {
  return (
    <div className="h-48 flex flex-col items-center justify-center text-center text-muted-foreground">
      <ListBulletIcon className="w-8 h-8 mb-2 opacity-40" />
      <p className="text-xs">
        {mode === 'live'
          ? 'Run an agent to see live execution here'
          : 'Pick a run from the History sidebar'}
      </p>
    </div>
  )
}
